import React, { useMemo, useReducer } from "react";
import { FullscreenTakeover, StepIndicator } from "@jsq/ds/core";
import { PayeeWorkflowModes } from "../Context/constants";
import {
  payeeReducer,
  initialPayeeState,
  BasePayeeReducerContextProvider,
  PayeeReducerDispatchProvider,
  payeeStepReducer,
  initialWorkflowState,
  PayeeWorkflowContextProvider,
  PayeeWorkflowReducerDispatchProvider,
} from "../Context/PayeeContext";
import { fullScreenTakeOverBodyStyles } from "../styles_vanilla.css";
import { PayeeWorkflowProps, PayeeWorkflowStep } from "../types";
import { getHeaderStepStatus } from "../utils";
import { StepNames } from "./constants";
import { InvestmentClientDetailsAndSupportingDocumentStep } from "./StepComponents/InvestmentClientDetailsAndSupportingDocumentStep";
import { InvestmentPayeeDetailsStep } from "./StepComponents/InvestmentPayeeDetailsStep";
import { InvestmentPaymentInstructionsStep } from "./StepComponents/InvestmentPaymentInstructionsStep";
import { InvestmentReviewStep } from "./StepComponents/InvestmentReviewStep";

export const PayeeInvestmentWorkflow: React.FC<PayeeWorkflowProps> = ({
  onExit,
  payeeState = initialPayeeState,
  workflowState = initialWorkflowState,
}: PayeeWorkflowProps) => {
  const [payeeData, dispatch] = useReducer(payeeReducer, payeeState);
  const [workflowData, dispatchWorkflow] = useReducer(payeeStepReducer, {
    ...workflowState,
    originalPayeeData: payeeState,
  });

  const workflowSteps: PayeeWorkflowStep[] = useMemo(() => {
    return [
      {
        index: 0,
        name: StepNames.ClientDetailsAndSupportingDocument,
        url: "0",
        status: getHeaderStepStatus(0, workflowData.stepNumber),
        component: InvestmentClientDetailsAndSupportingDocumentStep,
      },
      {
        index: 1,
        name: StepNames.PayeeDetails,
        url: "1",
        status: getHeaderStepStatus(1, workflowData.stepNumber),
        component: InvestmentPayeeDetailsStep,
      },
      {
        index: 2,
        name: StepNames.PaymentInstructions,
        url: "2",
        status: getHeaderStepStatus(2, workflowData.stepNumber),
        component: InvestmentPaymentInstructionsStep,
      },
      {
        index: 3,
        name: StepNames.Review,
        url: "3",
        status: getHeaderStepStatus(3, workflowData.stepNumber),
        component: InvestmentReviewStep,
      },
    ];
  }, [workflowData]);

  const currentStepDetails = useMemo(
    () => workflowSteps[workflowData.stepNumber],
    [workflowData, workflowSteps],
  );

  const StepComponent = currentStepDetails.component;

  return (
    <BasePayeeReducerContextProvider value={payeeData}>
      <PayeeReducerDispatchProvider value={dispatch}>
        <PayeeWorkflowContextProvider value={workflowData}>
          <PayeeWorkflowReducerDispatchProvider value={dispatchWorkflow}>
            <FullscreenTakeover>
              <FullscreenTakeover.Header
                title={
                  workflowData.mode === PayeeWorkflowModes.CREATE
                    ? "Create new payee · Investment"
                    : "Edit payee · Investment"
                }
                onExit={onExit}
              />
              <FullscreenTakeover.Subheader>
                <StepIndicator disableStepLinks steps={workflowSteps} />
              </FullscreenTakeover.Subheader>
              <FullscreenTakeover.Body className={fullScreenTakeOverBodyStyles}>
                <StepComponent />
              </FullscreenTakeover.Body>
            </FullscreenTakeover>
          </PayeeWorkflowReducerDispatchProvider>
        </PayeeWorkflowContextProvider>
      </PayeeReducerDispatchProvider>
    </BasePayeeReducerContextProvider>
  );
};

export default PayeeInvestmentWorkflow;
